/* ============================================================
   stats.js — everything the screens show is derived here from the
   raw season/team documents, so the numbers are computed one way.

   Rules (same as the design):
     earned per player  = Σ sold × profit
     retur              = max(0, alloc − sold)
     lagkassa           = Σ max(0, sold − alloc) × profit
     intjänat till SIF  = earned − lagkassa
     att betala         = Σ sold × price — what the player hands the leader
   ============================================================ */

export const kr = n => Math.round(n).toLocaleString('sv-SE') + ' kr';

/** The lottery types teams sell — everything but kansliet-only ones. */
export const teamTypes = season => (season.lotteryTypes || []).filter(t => !t.officeOnly);

const allocOf = (p, t, season) =>
  (p.alloc && p.alloc[t.id] != null ? p.alloc[t.id] : (season.allocDefaults?.[t.id] || 0));

/** A team with per-player cells and totals, ready to render. */
export function decorateTeam(team, season) {
  const types = teamTypes(season);
  let lots = 0, cal = 0, ret = 0, gross = 0, sales = 0, allocLots = 0, allocCal = 0, kassa = 0;
  const bt = {};
  for (const t of types) bt[t.id] = { id: t.id, name: t.name, isCal: t.kind === 'kalender', sold: 0, alloc: 0, ret: 0 };

  let toPayTeam = 0, paidSum = 0, paidCount = 0;
  const players = (team.players || []).map(p => {
    let earn = 0, toPay = 0;
    const cells = types.map(t => {
      const sold = (p.sold && p.sold[t.id]) || 0;
      const alloc = allocOf(p, t, season);
      const rt = Math.max(0, alloc - sold);
      const over = Math.max(0, sold - alloc);
      earn += sold * (t.profit || 0);
      kassa += over * (t.profit || 0);
      sales += sold * (t.price || 0);
      toPay += sold * (t.price || 0);
      if (t.kind === 'kalender') { cal += sold; allocCal += alloc; } else { lots += sold; allocLots += alloc; }
      ret += rt;
      bt[t.id].sold += sold; bt[t.id].alloc += alloc; bt[t.id].ret += rt;
      return { type: t.id, name: t.name, isCal: t.kind === 'kalender', value: sold, alloc, ret: rt };
    });
    gross += earn;
    toPayTeam += toPay;
    if (p.paid) { paidSum += toPay; paidCount++; }
    const denom = cells.reduce((a, c) => a + c.alloc, 0) || 1;
    const total = cells.reduce((a, c) => a + c.value, 0);
    const pct = Math.min(100, Math.round((total / denom) * 100));
    const lottAlloc = cells.reduce((a, c) => a + (c.isCal ? 0 : c.alloc), 0);
    const calAlloc = cells.reduce((a, c) => a + (c.isCal ? c.alloc : 0), 0);
    return {
      id: p.id, name: p.name, cells, earned: earn, toPay, pct,
      paid: !!p.paid,
      targetLabel: `${lottAlloc} lott · ${calAlloc} kal`,
    };
  });
  players.sort((a, b) => a.name.localeCompare(b.name, 'sv'));

  const club = Math.max(0, gross - kassa);
  const leaders = (team.leaders || []).map(l => (typeof l === 'string' ? { name: l } : l));
  const prog = Math.min(100, Math.round(((lots + cal) / ((allocLots + allocCal) || 1)) * 100));
  const byType = types.map(t => {
    const o = bt[t.id];
    return { ...o, met: o.alloc > 0 && o.sold >= o.alloc };
  });

  return {
    id: team.id, name: team.name,
    hasPassword: !!team.hasPassword,
    leaders,
    leadersLabel: leaders.map(l => l.name).join(', ') || '–',
    players, byType,
    done: !!team.done,
    lots, cal, ret, prog, sales, club, kassa,
    toPay: toPayTeam, paidSum, paidCount,
    allocLabel: `${allocLots} lotter + ${allocCal} kalendrar`,
  };
}

/**
 * Kansliets försäljning: no allocation, no returns, no lagkassa — every
 * lott sold earns its full profit for the club.
 */
export function officeStats(season) {
  const sold = season.office?.sold || {};
  let lots = 0, cal = 0, sales = 0, club = 0;
  const byType = (season.lotteryTypes || []).map(t => {
    const n = sold[t.id] || 0;
    if (t.kind === 'kalender') cal += n; else lots += n;
    sales += n * (t.price || 0);
    club += n * (t.profit || 0);
    return { id: t.id, name: t.name, price: t.price || 0, profit: t.profit || 0, sold: n,
      sales: n * (t.price || 0), club: n * (t.profit || 0) };
  });
  return { lots, cal, sales, club, byType };
}

/** Count, sales and profit for one bundle of lotter ({ typeId: n }). */
function bundle(items, types) {
  let lots = 0, cal = 0, sales = 0, club = 0;
  const parts = [];
  for (const t of types) {
    const n = items?.[t.id] || 0;
    if (!n) continue;
    if (t.kind === 'kalender') cal += n; else lots += n;
    sales += n * (t.price || 0);
    club += n * (t.profit || 0);
    parts.push(`${n} ${t.name}`);
  }
  return { lots, cal, sales, club, label: parts.join(' · ') };
}

/**
 * Kansliets utlämningar per dag, newest first: each day's hand-outs (already
 * newest first from the API). `lots`/`cal` count everything handed out that
 * day; `total`/`club` only kansliets own sales — extra lotter to a team are
 * the team's to sell. `legacy` is what was typed in as totals before sales
 * were dated, or null.
 */
export function officeDays(season) {
  const types = season.lotteryTypes || [];
  const days = [];
  for (const sale of season.office?.sales || []) {
    let day = days[days.length - 1];
    if (!day || day.date !== sale.date) {
      day = { date: sale.date, sales: [], lots: 0, cal: 0, total: 0, club: 0 };
      days.push(day);
    }
    const b = bundle(sale.items, types);
    day.sales.push({ ...sale, ...b });
    day.lots += b.lots; day.cal += b.cal;
    if (!sale.teamId) { day.total += b.sales; day.club += b.club; }
  }
  const legacy = bundle(season.office?.legacy, types);
  return { days, legacy: legacy.label ? legacy : null };
}

/** Extra lotter kansliet has handed one team: the total and each hand-out. */
export function teamHandouts(season, teamId) {
  const types = teamTypes(season);
  const list = (season.handouts?.[teamId] || []).map(x => ({ ...x, ...bundle(x.items, types) }));
  const items = {};
  for (const x of list) for (const [k, n] of Object.entries(x.items || {})) items[k] = (items[k] || 0) + n;
  return { ...bundle(items, types), items, list };
}

/** Every team that has extra lotter, in the season's team order. */
export function handoutsByTeam(season) {
  return (season.teams || [])
    .map(team => ({ team, ...teamHandouts(season, team.id) }))
    .filter(r => r.list.length);
}

/** Totals across a whole season: the (already decorated) teams plus kansliet. */
export function seasonTotals(dteams, season) {
  const t = { sales: 0, club: 0, kassa: 0, ret: 0, lots: 0, cal: 0, done: 0 };
  const retById = {};
  for (const d of dteams) {
    t.sales += d.sales; t.club += d.club; t.kassa += d.kassa; t.ret += d.ret;
    t.lots += d.lots; t.cal += d.cal; if (d.done) t.done++;
    for (const b of d.byType) retById[b.id] = (retById[b.id] || 0) + b.ret;
  }
  const office = officeStats(season);
  t.sales += office.sales; t.club += office.club; t.lots += office.lots; t.cal += office.cal;
  return {
    ...t,
    office,
    teamCount: dteams.length,
    retById,
    retCells: teamTypes(season).map(x => ({ id: x.id, name: x.name, ret: retById[x.id] || 0 })),
  };
}

/** One row per season for the "Försäljning per säsong" table. */
export function yearStats(state) {
  return [...state.years].sort((a, b) => b - a).map(y => {
    const season = state.seasons[y];
    const dteams = season.teams.map(t => decorateTeam(t, season));
    return { year: y, ...seasonTotals(dteams, season) };
  });
}

/** The value a team is sorted by in the admin table. */
export function sortValue(team, key) {
  switch (key) {
    case 'players': return team.players.length;
    case 'lots': return team.lots;
    case 'cal': return team.cal;
    case 'club': return team.club;
    case 'kassa': return team.kassa;
    case 'status': return team.done ? 1e9 : team.prog;
    default:
      if (key && key.startsWith('ret:')) {
        const b = team.byType.find(x => x.id === key.slice(4));
        return b ? b.ret : 0;
      }
      return team.name;
  }
}

export function sortTeams(dteams, key, dir) {
  return [...dteams].sort((x, y) => {
    const a = sortValue(x, key), b = sortValue(y, key);
    const c = typeof a === 'string' ? a.localeCompare(b, 'sv') : a - b;
    return dir === 'asc' ? c : -c;
  });
}
